import { z } from "zod";
import { StudioEffect } from "./studio-effects";

// §9.17 / D124 — Verbindliche Browser-Bearbeitung: der Browser führt via Pyodide die
// LITERAL gleiche ops.py aus (zweite RUNTIME, keine zweite Algorithmus-Quelle) und lädt
// das fertige Ergebnis über POST /v1/edits hoch. Diese Schemas validieren die Request-
// Metadaten an der Worker-Boundary (D12). WICHTIG: `params`/`opsHash` sind reine
// PROVENANCE — der PREIS hängt ausschließlich am `editKind` und wird serverseitig
// fail-closed aus dem aktiven Price-Book berechnet (D34/D44; nie aus dem Request).

// D139 (Studio): `"studio"` ist der GENERISCHE Edit-Kind — sein Preis & Ergebnis-Kind hängen
// NICHT mehr am editKind, sondern an der validierten `steps`-Liste bzw. `resultKind` (s. u.).
// Die Alt-Werte BLEIBEN (Reads alter Edit-Assets tragen sie in `assets.metadata`).
export const EditKind = z.enum(["image-key", "spritesheet-rekey", "spritesheet-loopfix", "studio"]);
export type EditKind = z.infer<typeof EditKind>;

/** Die FIXEN (D124/D130) Edit-Kinds — `studio` ist step-getrieben und NICHT Teil dieser Records. */
export const LEGACY_EDIT_KINDS = ["image-key", "spritesheet-rekey", "spritesheet-loopfix"] as const;
export type LegacyEditKind = (typeof LEGACY_EDIT_KINDS)[number];

/**
 * Studio-Schritte (D139) in KANONISCHER Ausführungs-/Preis-Reihenfolge — spiegelt die Op-Kette
 * timeline-select → [RIFE-Naht] → chroma_key → transparency_crop → spritesheet_pack. Jeder
 * Schritt ist ein FLAT-Preis-Term (Pflicht: `editPricePlan` mit leeren inputs kann keine
 * Metrik-Terme — s. apps/shared/src/edits.ts). `timeline-cut` ist die NEUE Capability (Book v7,
 * flat 1); die übrigen sind bestehende Knoten. `loop` zählt nur bei aktiver RIFE-Naht.
 */
export const STUDIO_STEP_ORDER = [
  "timeline-cut",
  "loop",
  "chroma-key",
  "transparency-crop",
  "spritesheet-pack",
] as const;
export const StudioStep = z.enum(STUDIO_STEP_ORDER);
export type StudioStep = z.infer<typeof StudioStep>;
/** Set-Form der Whitelist (Validierung am Worker, SSOT). */
export const STUDIO_STEP_WHITELIST: ReadonlySet<string> = new Set(STUDIO_STEP_ORDER);

/**
 * editKind → Capability-Knoten, die der Browser-Edit tatsächlich ausführt (= Preis-Basis).
 * SSOT für Server (storeEdit) UND Frontend-Preis-Vorschau (PREVIEW_PRICE_BOOK, D34). NUR für die
 * FIXEN Kinds — `studio` rechnet step-getrieben (STUDIO_STEP_ORDER). `spritesheet-rekey` läuft
 * BEWUSST OHNE `loop`: die Frame-Auswahl des Original-Jobs wird als Präfix (`packMeta.frameCount`)
 * WIEDERVERWENDET, nicht neu berechnet — bereits bezahlte loop-Arbeit wird nicht doppelt
 * berechnet (D124). `spritesheet-loopfix` (D130) trägt `loop` dagegen ZU RECHT: die Erkennung
 * läuft im Browser NEU und der `loop`(4)-Book-Eintrag deckt den RIFE-Naht-COGS (D122).
 */
export const EDIT_KIND_CAPABILITIES: Record<LegacyEditKind, readonly string[]> = {
  "image-key": ["chroma-key"],
  "spritesheet-rekey": ["chroma-key", "transparency-crop", "spritesheet-pack"],
  "spritesheet-loopfix": ["loop", "chroma-key", "transparency-crop", "spritesheet-pack"],
};

/**
 * editKind → Ergebnis-AssetKind. Das Edit-Ergebnis ist immer ein PNG (Magic-Byte-validiert);
 * der Asset-`kind` kommt aus der Edit-SEMANTIK, nicht aus den Bytes (ein Sheet ist auch ein PNG).
 * NUR FIXE Kinds — `studio` liefert seinen Ergebnis-Kind über `resultKind` (image|spritesheet).
 */
export const EDIT_KIND_ASSET_KIND = {
  "image-key": "image",
  "spritesheet-rekey": "spritesheet",
  "spritesheet-loopfix": "spritesheet",
} as const satisfies Record<LegacyEditKind, "image" | "spritesheet">;

/** Tuning-Werte, die im Browser in `ops.chroma_key` gingen (Provenance; offen für Zusatzfelder). */
export const EditChromaParams = z
  .object({
    keyColor: z.enum(["auto", "green", "blue"]),
    tolerance: z.number().min(0).max(100),
  })
  .passthrough();
export type EditChromaParams = z.infer<typeof EditChromaParams>;

/**
 * Pack-Meta des im Browser gepackten Sheets — gleicher Vertrag wie `signals.pack.meta`
 * (D68), damit das gespeicherte Sheet wieder als Spritesheet abspielbar ist. `frameCount`
 * ist beim Re-Key zugleich die PRÄFIX-Länge der wiederverwendeten Frame-Auswahl (D124).
 */
export const EditPackMeta = z
  .object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
    frameCount: z.number().int().positive(),
    frameWidth: z.number().int().positive(),
    frameHeight: z.number().int().positive(),
    fps: z.number().positive(),
    loop: z.boolean(),
  })
  .passthrough();
export type EditPackMeta = z.infer<typeof EditPackMeta>;

/**
 * Vermutetes Spritesheet-Grid (D139) — der Rückgabe-Vertrag von `ops.detect_grid` (SSOT beide
 * Runtimes). `method='none'` ⇒ Einzelbild / manuelle Grid-UI (Stufe C); `detectedKey` ist die
 * Stufe-B-Chroma-Farbe (belegt die BG-Remove-Op vor). Reine Provenance/UI-Übergabe.
 */
export const GridGuess = z
  .object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
    frameCount: z.number().int().nonnegative(),
    frameWidth: z.number().int().positive(),
    frameHeight: z.number().int().positive(),
    method: z.enum(["alpha", "chroma", "none"]),
    confidence: z.number().min(0).max(1),
    detectedKey: z.array(z.number().int().min(0).max(255)).length(3).nullable().optional(),
  })
  .passthrough();
export type GridGuess = z.infer<typeof GridGuess>;

/**
 * EIN Timeline-Segment als HALB-OFFENER Bereich `[from, to)` (Python-Slice-nativ, D139). Die
 * geordnete Liste ist die OUTPUT-Reihenfolge (Umordnen/Duplizieren). Indizes werden serverseitig
 * geklemmt (`ops.frames_select_segments`) — hier nur Typ-/Vorzeichen-Schutz. Die UI zeigt
 * inklusive Klammern und konvertiert in `studioPlan.ts`.
 */
export const StudioSegment = z.tuple([
  z.number().int().nonnegative(), // from (inklusive)
  z.number().int().nonnegative(), // to (exklusiv)
]);
export type StudioSegment = z.infer<typeof StudioSegment>;

/** Request-Metadaten für POST /v1/edits (Bytes kommen als Roh-Body, D61-Muster). */
export const EditRequest = z
  .object({
    editKind: EditKind,
    /** Quell-Asset (Ownership-geprüft im RPC) — Bild bzw. das source-video des Sheets. */
    sourceAssetId: z.string().uuid(),
    /** sha256-hex über den ops.py-Quelltext, den der Browser ausführte (reine Provenance). */
    opsHash: z
      .string()
      .min(8)
      .max(128)
      .regex(/^[0-9a-f]+$/i, "opsHash: hex erwartet"),
    params: EditChromaParams,
    packMeta: EditPackMeta.optional(),
    // ── D139 Studio (nur bei editKind='studio' belegt) ──
    /** Tatsächlich gelaufene Schritte (Preis-Basis) — Teilmenge + Reihenfolge von STUDIO_STEP_ORDER. */
    steps: z.array(StudioStep).optional(),
    /** Ergebnis-Kind des Studio-Saves (image=Einzelframe-PNG, spritesheet=gepacktes Sheet). */
    resultKind: z.enum(["image", "spritesheet"]).optional(),
    /** Timeline-Segmente (Provenance + identischer Server-Re-Run für WebM-Export). */
    segments: z.array(StudioSegment).optional(),
    /** fps der Quell-Frame-Sequenz (Provenance; Video=24, Sheet=packMeta.fps). */
    sourceFps: z.number().positive().optional(),
    /** Grid-Erkennung beim Laden (Provenance, falls das Sheet entpackt wurde). */
    gridGuess: GridGuess.optional(),
    /** Komponierter Effekt-Stack (§9.17, Studio) — reine PROVENANCE: preis-NEUTRAL (der Save-Preis
     *  hängt an `steps`, nicht an den Effekten). In den PNG-Bytes bereits gebacken. */
    effects: z.array(StudioEffect).optional(),
    /** Ziel-Ordner (null = Inbox/untargeted, §9.4). */
    folderId: z.string().uuid().nullable().default(null),
  })
  .superRefine((val, ctx) => {
    if (val.editKind === "studio") {
      // Steps sind die Preis-Basis — leer wäre ein Gratis-Save (RPC erlaubt price=0).
      if (!val.steps || val.steps.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "studio braucht mindestens einen step (D139)" });
        return;
      }
      // Eindeutig + in kanonischer Reihenfolge (strikt aufsteigender STUDIO_STEP_ORDER-Index) —
      // erzwingt die Op-Kette und verhindert doppelte/umsortierte Preis-Terme.
      const idx = val.steps.map((s) => STUDIO_STEP_ORDER.indexOf(s));
      for (let i = 1; i < idx.length; i++) {
        if ((idx[i] ?? -1) <= (idx[i - 1] ?? -1)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "studio steps: eindeutig + in kanonischer Reihenfolge" });
          break;
        }
      }
      if (!val.resultKind) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "studio braucht resultKind (D139)" });
        return;
      }
      const packsSheet = val.steps.includes("spritesheet-pack");
      // spritesheet-pack ⇔ resultKind=spritesheet (Einzelframe-PNG wird NICHT gepackt).
      if (packsSheet !== (val.resultKind === "spritesheet")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "studio: spritesheet-pack genau dann, wenn resultKind=spritesheet",
        });
      }
      // Ein gepacktes Sheet ohne packMeta wäre nicht abspielbar (Provenance fehlte).
      if (val.resultKind === "spritesheet" && !val.packMeta) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "studio-Sheet braucht packMeta (D139)" });
      }
      return;
    }
    // Ohne packMeta wäre ein Browser-gepacktes Sheet nicht abspielbar (und die Provenance fehlte).
    if ((val.editKind === "spritesheet-rekey" || val.editKind === "spritesheet-loopfix") && !val.packMeta) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${val.editKind} braucht packMeta (D124/D130)` });
    }
  });
export type EditRequest = z.infer<typeof EditRequest>;

/**
 * Shape der Edit-Provenance in `assets.metadata` (jsonb). Optionale VERFEINERUNG für
 * Reads/Tests — KEIN Pflicht-Parse an der Asset-Boundary (Upload-Assets haben andere
 * metadata). `parent_asset_id` bleibt der Bild→Animation-Relation vorbehalten (§9.2):
 * die Quell-Beziehung lebt hier in den Metadaten.
 */
export const EditProvenance = z
  .object({
    origin: z.literal("browser-edit"),
    editKind: EditKind,
    sourceAssetId: z.string().uuid(),
    opsHash: z.string(),
    params: z.record(z.string(), z.unknown()),
    /** Bestätigter MIME-Typ + Größe + sha256 der ECHTEN Ergebnis-Bytes (validateUpload). */
    mime: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    checksum: z.string(),
    packMeta: EditPackMeta.optional(),
    // D139 Studio (passthrough ⇒ abwärtskompatibel zu Alt-Edit-Assets):
    steps: z.array(StudioStep).optional(),
    resultKind: z.enum(["image", "spritesheet"]).optional(),
    segments: z.array(StudioSegment).optional(),
    sourceFps: z.number().positive().optional(),
    gridGuess: GridGuess.optional(),
    effects: z.array(StudioEffect).optional(),
  })
  .passthrough();
export type EditProvenance = z.infer<typeof EditProvenance>;
